import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/* Poll schedule (ms): quick at first — the webhook/redirect usually lands in a
   few seconds — then ease off into a long tail so even a slow webhook confirms
   while the user watches (~70s total before we fall back to the timeout state). */
const DELAYS = [1500, 2000, 2000, 2500, 2500, 3000, 3000, 4000, 4000, 5000, 5000, 6000, 8000, 8000, 10000];

/* Landing page after Authorize.Net's hosted checkout / card manager. */
export default function BillingReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const aliveRef = useRef(true);

  const tid = params.get("tid");
  const isCard = !!params.get("card");

  const [phase, setPhase] = useState("confirming"); // confirming | card | paid | failed | timeout
  const [detail, setDetail] = useState("");
  const [receipt, setReceipt] = useState("");
  const [rechecking, setRechecking] = useState(false);

  // One verification poll. Returns true once we reach a terminal (paid/failed) state.
  const runVerify = useCallback(async () => {
    try {
      const { data } = await api.post(`/billing/checkout/${tid}/verify`);
      if (data.status === "paid") {
        setReceipt(data.transaction?.receipt_number || "");
        setPhase("paid");
        await refreshMe();
        return true;
      }
      if (data.status === "failed") {
        setDetail(data.transaction?.reason || "The payment was declined or cancelled.");
        setPhase("failed");
        return true;
      }
    } catch (_) { /* transient — keep polling */ }
    return false;
  }, [tid, refreshMe]);

  useEffect(() => {
    aliveRef.current = true;
    (async () => {
      if (isCard) {
        setPhase("card");
        try {
          await api.post("/billing/sync-card");
          toast.success("Card on file updated");
        } catch (e) {
          toast.error("Couldn't read the updated card yet", { description: formatApiError(e) });
        }
        await refreshMe();
        if (aliveRef.current) navigate("/billing", { replace: true });
        return;
      }
      if (!tid) { navigate("/billing", { replace: true }); return; }
      for (let i = 0; i < DELAYS.length && aliveRef.current; i++) {
        if (await runVerify()) return;
        await sleep(DELAYS[i]);
      }
      if (aliveRef.current && (await runVerify())) return;
      if (aliveRef.current) setPhase("timeout");
    })();
    return () => { aliveRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After success, send the user back to billing automatically (with the paid
  // transaction id so the Billing page shows the "Transaction approved" popup).
  useEffect(() => {
    if (phase !== "paid") return;
    const dest = tid ? `/billing?paid=${tid}` : "/billing";
    const t = setTimeout(() => { if (aliveRef.current) navigate(dest, { replace: true }); }, 3000);
    return () => clearTimeout(t);
  }, [phase, navigate, tid]);

  const recheckNow = async () => {
    setRechecking(true);
    const done = await runVerify();
    setRechecking(false);
    if (!done) toast.message("Still pending — Authorize.Net hasn't confirmed it yet. Give it a moment and try again.");
  };

  const Shell = ({ tone, icon, title, children, testid }) => (
    <div
      className="rounded-lg border border-slate-200 bg-white p-10 max-w-lg flex flex-col items-center text-center gap-3"
      data-testid="billing-return"
    >
      <div
        className="h-10 w-10 rounded-md flex items-center justify-center text-white"
        style={{ background: tone }}
      >
        {icon}
      </div>
      <h1 className="text-xl font-semibold text-slate-900 font-display">{title}</h1>
      <div className="text-sm text-slate-600 flex flex-col items-center gap-3" data-testid={testid}>
        {children}
      </div>
    </div>
  );

  if (phase === "paid") {
    return (
      <Shell tone="#16a34a" icon={<CheckCircle2 className="h-5 w-5" />} title="Payment confirmed" testid="billing-return-message">
        <p className="flex items-center gap-2" data-testid="billing-return-paid">
          Thank you — your account has been updated.
          {receipt ? <span className="text-slate-500">Receipt {receipt} is ready to download.</span> : null}
        </p>
        <Button size="sm" onClick={() => navigate(tid ? `/billing?paid=${tid}` : "/billing", { replace: true })} data-testid="billing-return-go-billing">
          Go to billing
        </Button>
      </Shell>
    );
  }

  if (phase === "failed") {
    return (
      <Shell tone="#dc2626" icon={<XCircle className="h-5 w-5" />} title="Payment didn't go through" testid="billing-return-message">
        <p data-testid="billing-return-failed">{detail}</p>
        <Button size="sm" variant="outline" onClick={() => navigate("/billing", { replace: true })} data-testid="billing-return-back">
          Back to billing
        </Button>
      </Shell>
    );
  }

  if (phase === "timeout") {
    return (
      <Shell tone="#d97706" icon={<Clock className="h-5 w-5" />} title="Still confirming" testid="billing-return-message">
        <p data-testid="billing-return-timeout">
          Authorize.Net is taking a little longer than usual to confirm this payment. It will appear on your
          billing page automatically — you can check now or head back.
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={recheckNow} disabled={rechecking} data-testid="billing-return-recheck">
            {rechecking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check now"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate("/billing", { replace: true })} data-testid="billing-return-back">
            Back to billing
          </Button>
        </div>
      </Shell>
    );
  }

  // confirming | card
  return (
    <Shell tone="hsl(var(--brand))" icon={<ShieldCheck className="h-5 w-5" />} title="Almost there" testid="billing-return-message">
      <p className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        {isCard ? "Syncing your updated card…" : "Confirming your payment with Authorize.Net…"}
      </p>
    </Shell>
  );
}
